<template>
  <RegionIndexPage
    :all-regions="allRegions"
    :region="indexRegion"
    :categories="categories"
    :search-keywords-of-region="searchKeywordsOfRegion"
    :history-key-words="historyKeyWords"
    :card-mode="cardMode"
  />
</template>

<script setup lang="ts">
import notificationComposition from '@/composition/notification'
import { useABTestingStore } from '@/store-pinia/abtesting'
import { useRegionStore } from '@/store-pinia/region'
import { RegionIdMap } from '@/view-models/region'

import RegionIndexPage from '@/components/page/regions/region-index.vue'

definePageMeta({
  middleware: 'lang-region-alias',
})

const { pushGiftExpiringNotification } = notificationComposition()
const { $store: store, $auth } = useNuxtApp()
const { t, locale } = useI18n()
const config = useRuntimeConfig()
const req = useRequestURL()

const regionStore = useRegionStore()
const abTestingStore = useABTestingStore()
const { regionDetectInfo } = storeToRefs(regionStore)

const indexRegion = computed(() => store.getters['region/indexRegion'])

useHead(() => {
  const regionTitle = indexRegion.value?.name || ''
  const pageTitle = `${regionTitle} | ${t('funnow_title')}`
  return {
    title: pageTitle,
    meta: [{ hid: 'title', name: 'title', content: pageTitle }],
  }
})

const allRegions = computed(() => store.getters['region/allRegions'])
const categories = computed(() => store.getters['region/categories'])
const searchKeywordsOfRegion = computed(() => store.getters['search/searchKeywordsOfRegion'])
const historyKeyWords = computed(() => store.getters['search/searchKeywordsOfHistory'])
const cardMode = computed(() => abTestingStore.cardMode)
const loggedInUser = computed(() => $auth.user)

try {
  const filters = store.getters['filter/filters']
  if (Object.keys(filters).length > 0) {
    store.dispatch('filter/resetFilterState')
  }
  /** 外來 domain 首頁導回 funnow domain */
  const host = req.host
  if (store.getters.isFromForeignDomain && host && !config.public.BASE_URL.includes(host)) {
    navigateTo(`${config.public.BASE_URL}/${locale.value}`, { external: true })
  }
} catch (e) {
  console.error(e)
}

if (!indexRegion.value?.id) {
  const regionId = regionDetectInfo.value?.id ?? RegionIdMap.Taipei
  await getRegionInfo(regionId)
}

watch(loggedInUser, (newUser, oldUser) => {
  if (newUser && !oldUser) {
    pushGiftExpiringNotification(newUser)
  }
})
watch(locale, () => {
  if (indexRegion.value?.id) {
    getRegionInfo(indexRegion.value.id)
  }
})

onMounted(() => {
  setNotification()
})
onBeforeUnmount(() => {
  store.dispatch('notification/resetNotification')
})

function setNotification() {
  if (loggedInUser.value) {
    pushGiftExpiringNotification(loggedInUser.value)
  }
}
function getRegionInfo(regionId: number) {
  return store.dispatch('region/getRegion', regionId)
}
</script>

<style scoped lang="scss" src="@/assets/sass/pages/region-index.scss"></style>
